import React, { useState } from 'react'
import OfferModal from './OfferModal'

export default function SoftwareDevelopmentExpertise() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')

  const handleOpenModal = (type) => {
    setModalType(type)
    setIsModalOpen(true)
    // GA4: Track CTA engagement
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'cta_click', {
        event_category: 'Software Development',
        event_label: type,
        page_location: window.location.href
      })
    }
  }

  // Technology Groups Data
  const techGroups = [
    {
      category: 'Frontend',
      technologies: [
        { name: 'React', icon: 'react/react-original.svg' },
        { name: 'Next.js', icon: 'nextjs/nextjs-original.svg' },
        { name: 'Vue.js', icon: 'vuejs/vuejs-original.svg' },
        { name: 'TypeScript', icon: 'typescript/typescript-original.svg' }
      ]
    },
    {
      category: 'Backend',
      technologies: [
        { name: 'Node.js', icon: 'nodejs/nodejs-original.svg' },
        { name: 'Python', icon: 'python/python-original.svg' },
        { name: 'Java Spring', icon: 'spring/spring-original.svg' },
        { name: 'PHP', icon: 'php/php-original.svg' }
      ]
    },
    {
      category: 'Databases',
      technologies: [
        { name: 'MySQL', icon: 'mysql/mysql-original-wordmark.svg' },
        { name: 'PostgreSQL', icon: 'postgresql/postgresql-original.svg' },
        { name: 'MongoDB', icon: 'mongodb/mongodb-original.svg' },
        { name: 'Redis', icon: 'redis/redis-original.svg' }
      ]
    },
    {
      category: 'Cloud Platforms',
      technologies: [
        { name: 'AWS', icon: 'amazonwebservices/amazonwebservices-original-wordmark.svg' },
        { name: 'GCP', icon: 'googlecloud/googlecloud-original.svg' },
        { name: 'Azure', icon: 'azure/azure-original.svg' },
        { name: 'DigitalOcean', localIcon: 'digitalocean.svg' }
      ]
    },
    {
      category: 'DevOps & CI/CD',
      technologies: [
        { name: 'Docker', icon: 'docker/docker-original.svg' },
        { name: 'Kubernetes', icon: 'kubernetes/kubernetes-plain.svg' },
        { name: 'Terraform', icon: 'terraform/terraform-original.svg' },
        { name: 'GitLab CI', icon: 'gitlab/gitlab-original.svg' }
      ]
    },
    {
      category: 'Artificial Intelligence',
      technologies: [
        { name: 'LLMs', localIcon: 'llm.svg', isCustom: true, emoji: '🤖' },
        { name: 'RAG', localIcon: 'rag.svg', isCustom: true, emoji: '🔍' },
        { name: 'AI Agents', localIcon: 'agents.svg', isCustom: true, emoji: '🧠' },
        { name: 'Automation', localIcon: 'automation.svg', isCustom: true, emoji: '⚡' }
      ]
    }
  ]

  // Process Steps Data
  const processSteps = [
    {
      num: '01',
      title: 'Discovery',
      desc: 'We begin by analyzing your business goals, target users, and key performance indicators. Through collaborative workshops, we define the product vision, validate assumptions, and map user journeys to lay a firm software foundation.'
    },
    {
      num: '02',
      title: 'Requirements & Architecture',
      desc: 'Our architects design system blueprints, choose the right technology stack, and draft detailed project specifications. This step establishes data models, database selection, API contracts, and security boundaries.'
    },
    {
      num: '03',
      title: 'UI/UX & Development',
      desc: 'Our designers create interactive prototypes to define look-and-feel. Once finalized, our engineers write clean, maintainable code using version-controlled agile sprints to bring designs to life.'
    },
    {
      num: '04',
      title: 'Testing & Security',
      desc: 'We apply rigorous automated unit, integration, and load testing alongside static security scans (SAST/DAST). This guarantees code reliability and prevents security leaks before release.'
    },
    {
      num: '05',
      title: 'CI/CD & Deployment',
      desc: 'We deploy automated continuous integration and delivery pipelines to push builds seamlessly to staging and production. Using blue-green or canary rollouts, we ensure zero-downtime updates.'
    },
    {
      num: '06',
      title: 'Monitoring & Optimization',
      desc: 'We integrate observability tools like Datadog, Prometheus, and Grafana to track user traffic, server resources, and query performance, optimizing response times and resource use.'
    },
    {
      num: '07',
      title: 'Ongoing Support',
      desc: 'Post-deployment, our team provides continuous maintenance, patches security dependencies, and optimizes system operations. We ensure your application scales smoothly alongside business growth.'
    }
  ]

  // FAQs Data
  const faqs = [
    {
      q: 'What does custom software development include?',
      a: 'Custom software development includes the complete engineering lifecycle tailored to your business: requirements analysis, system architecture design, UI/UX prototyping, full-stack engineering, quality assurance testing, secure API integrations, and cloud deployment configuration.'
    },
    {
      q: 'How much does custom software development cost?',
      a: 'The cost depends on scope, complexity, and technology selection. A simple SaaS prototype or MVP may range from $20,000 to $50,000, whereas scaling complex enterprise platforms or deep AI integrations typically ranges from $75,000 to $250,000+. We provide granular transparent estimation upfront.'
    },
    {
      q: 'How long does software development take?',
      a: 'A minimal viable product (MVP) takes approximately 8 to 12 weeks. Large-scale enterprise systems, complex API infrastructures, and intelligent agent integrations require 4 to 8 months. We deliver working increments every 2 weeks using agile sprints.'
    },
    {
      q: 'Do you develop SaaS applications?',
      a: 'Yes, we are a specialized SaaS development company. We build multi-tenant architectures, configure secure federated authentication, integrate payment systems like Stripe, design developer APIs, and set up automatic subscription billing workflows.'
    },
    {
      q: 'Can you modernize an existing application?',
      a: 'Yes. We perform legacy application modernization by containerizing monolithic systems into microservices, migrating databases, upgrading older frameworks, and improving frontends with React or Next.js to restore high performance.'
    },
    {
      q: 'Do you provide ongoing maintenance and DevOps?',
      a: 'Yes, we provide 24/7 managed reliability support. We maintain server infrastructure, manage automated backups, apply security patches, monitor uptime, and optimize database queries to ensure zero-downtime operations.'
    },
    {
      q: 'Can you integrate AI into an existing application?',
      a: 'Yes, we develop custom AI integrations. We build Large Language Model (LLM) applications, inject Retrieval-Augmented Generation (RAG) capabilities with vector databases, connect custom AI agents, and set up intelligent workflow automation.'
    }
  ]

  const [activeFaq, setActiveFaq] = useState(null)
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index)
  }

  return (
    <div className="font-inter bg-slate-50 text-slate-800 selection:bg-[#00cfff]/30">
      
      {/* ── 1. Hero Section ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b3b8f] via-[#1048a8] to-[#041a4a] text-white py-28 md:py-36">
        <style>{`
          @keyframes shine {
            to { background-position: 200% center; }
          }
          @keyframes hexDash {
            to { stroke-dashoffset: -200; }
          }
          @keyframes hexPan {
            from { transform: translateY(0); }
            to { transform: translateY(-103.923px); }
          }
        `}</style>
        
        {/* Animated Hexagon Network Background */}
        <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-screen overflow-hidden">
          <svg className="w-full h-[150%] animate-[hexPan_15s_linear_infinite]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hexGrid" x="0" y="0" width="60" height="103.923" patternUnits="userSpaceOnUse">
                <path d="M30 0 L60 17.32 L60 51.96 L30 69.28 L0 51.96 L0 17.32 Z" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <path d="M30 103.92 L60 86.6 L60 51.96 L30 69.28 L0 51.96 L0 86.6 Z" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <path d="M60 51.96 L90 34.64 L90 0 L60 -17.32 L30 0 L30 34.64 L60 51.96 Z" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <path d="M30 0 L60 17.32 L60 51.96 L30 69.28 L0 51.96 L0 86.6 L30 103.92" fill="none" stroke="#00cfff" strokeWidth="1.5" strokeDasharray="30 170" style={{ animation: 'hexDash 3s linear infinite' }} />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexGrid)" />
          </svg>
        </div>

        {/* Ambient Gradient Glows */}
        <div className="absolute inset-0 pointer-events-none mix-blend-soft-light">
          <div className="absolute top-10 left-10 w-48 h-48 bg-[#00cfff]/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-md px-5 py-2 rounded-full mb-8 shadow-lg">
              <div className="w-2.5 h-2.5 bg-[#00cfff] rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold tracking-wide uppercase">Software Engineering Services</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-extrabold mb-8 leading-tight tracking-tight">
              Custom Software Development Services
            </h1>
            
            <p className="text-lg md:text-xl mb-12 text-blue-100/90 leading-relaxed font-normal max-w-3xl mx-auto">
              Build secure, scalable and high-performance software tailored to your business requirements. NexGenium helps startups and businesses turn ideas into production-ready applications.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button 
                onClick={() => handleOpenModal('Free Consultation')}
                className="group inline-flex items-center justify-center px-10 py-4 bg-white text-[#0b3b8f] rounded-xl font-bold hover:bg-slate-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
              >
                Book a Free Consultation
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button 
                onClick={() => handleOpenModal('Get a Quote')}
                className="group inline-flex items-center justify-center px-10 py-4 border border-white/40 bg-black/20 backdrop-blur-md text-white rounded-xl font-bold hover:bg-white/30 transition-all duration-300 shadow-xl hover:-translate-y-0.5"
              >
                Request a Project Estimate
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Build Software Around Your Business Section ── */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">
            Build Software Around Your Business
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto font-medium">
            At NexGenium, we don't believe in boilerplate templates or forcing technologies on your business. We analyze your operational challenges, workflow bottlenecks, and scaling aspirations first. Our team tailors custom architectures that integrate seamlessly into your current software eco-system, driving real efficiency and revenue growth.
          </p>
        </div>
      </section>

      {/* ── 3. Our Software Development Services Section (Intro) ── */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Our Software Development Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              Enterprise-grade development capability addressing complex architectural challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* SaaS Development */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-6">🚀</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">SaaS Development</h3>
              <p className="text-slate-600 leading-relaxed mb-6 font-medium">
                Engineered for continuous scaling and cloud resilience. We specialize in configuring multi-tenant architectures, federated authentication systems, advanced billing pipelines, robust client APIs, dynamic database sharding, and real-time observability telemetry.
              </p>
              <ul className="space-y-2 text-sm text-slate-500 font-semibold">
                <li>• Multi-tenant database partitioning</li>
                <li>• Stripe & subscription billing integration</li>
                <li>• Scalable API gateways & rate limiting</li>
                <li>• Real-time system log monitoring</li>
              </ul>
            </div>

            {/* Web & Enterprise Application Development */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 lg:col-span-2">
              <div className="text-3xl mb-6">🏢</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Web & Enterprise Application Development</h3>
              <p className="text-slate-600 leading-relaxed mb-6 font-medium">
                We craft high-performance web applications using modern stacks like React, Next.js, and Vue.js on the frontend, supported by Node.js or Python backend layers. For enterprise operations, we focus on safe software integrations, strict database security, high-availability horizontal scaling, and comprehensive performance observability.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-500 font-semibold">
                <div>
                  <li>• Next.js SSR & Vue.js SPA designs</li>
                  <li>• Node.js & Python backend services</li>
                  <li>• Secure Enterprise resource portals</li>
                </div>
                <div>
                  <li>• Relational & NoSQL cluster configurations</li>
                  <li>• Prometheus & Datadog metrics</li>
                  <li>• Strict compliance-ready architectures</li>
                </div>
              </div>
            </div>

            {/* API & Backend Development */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-6">⚙️</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">API & Backend Development</h3>
              <p className="text-slate-600 leading-relaxed mb-6 font-medium">
                We architect bulletproof RESTful APIs and GraphQL interfaces. Our backend engineering emphasizes high-throughput data processing, decoupled microservices, secure database modeling, and maximum server uptime.
              </p>
              <ul className="space-y-2 text-sm text-slate-500 font-semibold">
                <li>• REST & GraphQL API design</li>
                <li>• Microservices architecture</li>
                <li>• Redis caching & queuing layers</li>
                <li>• Secure JWT and OAuth 2.0 auth</li>
              </ul>
            </div>

            {/* eCommerce Development */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-6">🛒</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">eCommerce Development</h3>
              <p className="text-slate-600 leading-relaxed mb-6 font-medium">
                Fast-loading eCommerce stores designed for customer conversions. We develop custom monolithic and headless setups utilizing Magento 2, WooCommerce, and Shopify, maintaining highly optimized checkouts.
              </p>
              <ul className="space-y-2 text-sm text-slate-500 font-semibold">
                <li>• Monolithic & Headless eCommerce</li>
                <li>• Speed-optimized checkout engines</li>
                <li>• Dynamic inventory synchronization</li>
                <li>• Scalable payment gateway setups</li>
              </ul>
            </div>

            {/* AI-Powered Software Development */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-6">🧠</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">AI-Powered Software Development</h3>
              <p className="text-slate-600 leading-relaxed mb-6 font-medium">
                Integrating AI smarts directly into your user flows. We develop LLM integrations, Retrieval-Augmented Generation (RAG) pipelines using vector databases, autonomous AI agents, and custom workflow automations.
              </p>
              <ul className="space-y-2 text-sm text-slate-500 font-semibold">
                <li>• LLM prompt tuning & integration</li>
                <li>• Vector search (Pinecone, pgvector)</li>
                <li>• Smart autonomous customer agents</li>
                <li>• Automated content & invoice scraping</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── 4. Our Software Development Process Section ── */}
      <section className="py-24 bg-white border-y border-slate-100">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Our Software Development Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              A structured lifecycle approach prioritizing rapid sprints, security checks, and robust deployment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <div key={step.num} className="relative p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors">
                <div className="text-4xl font-extrabold text-[#00cfff] mb-4">{step.num}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-medium">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Technology Expertise Section (Moved down) ── */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Technology Expertise
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              We leverage modern, production-proven libraries, frameworks, and infrastructure tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {techGroups.map((group) => (
              <div key={group.category} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h3 className="text-xl font-bold text-[#0b3b8f] mb-6 pb-2 border-b border-slate-100">
                  {group.category}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {group.technologies.map((tech) => (
                    <div key={tech.name} className="flex items-center gap-3 p-2 rounded-xl bg-slate-50 hover:bg-blue-50/50 transition-colors">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        {tech.isCustom ? (
                          <span className="text-xl">{tech.emoji}</span>
                        ) : (
                          <img 
                            src={tech.localIcon ? `/tech-logos/${tech.localIcon}` : `https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/${tech.icon}`} 
                            className="max-h-full max-w-full object-contain" 
                            alt={tech.name} 
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        )}
                      </div>
                      <span className="text-sm font-bold text-slate-700">{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Why Choose NexGenium Section ── */}
      <section className="py-24 bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Why Choose NexGenium?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              We stand apart through our elite technical pedigree, transparent reporting, and strict quality control.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 rounded-2xl bg-slate-50 text-center">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Elite Engineers</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-medium">
                Our team is composed of seasoned software architects and engineers with years of hands-on expertise building production systems at scale.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-slate-50 text-center">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Security-First</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-medium">
                OWASP security checks, dependency scanning, and safe coding policies are baked directly into our continuous delivery pipelines.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-slate-50 text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Observability Built-In</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-medium">
                Every system we launch is pre-configured with tracing, log aggregation, and system telemetry to monitor health and trace latency anomalies.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-slate-50 text-center">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Agile & Transparent</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-medium">
                No secrets, no surprises. Access our Jira boards and Slack channels directly, keeping track of every line of code deployed in bi-weekly sprints.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. Software Development Case Studies Section ── */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Software Development Case Studies
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
              Real success stories showing our design, engineering, and execution in action.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm flex flex-col justify-between">
              <div className="p-8">
                <span className="text-xs font-bold text-[#00cfff] tracking-wide uppercase mb-2 block">FinTech SaaS</span>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Scalable Ledger Billing Pipeline</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-medium mb-6">
                  Engineered a multi-tenant subscription ledger processing over 10,000 requests per second. Migrated legacy relational constraints to a Redis-backed queue core, resulting in major latency reductions.
                </p>
                <div className="text-3xl font-extrabold text-[#0b3b8f] mb-1">99.99%</div>
                <div className="text-xs text-slate-500 font-bold uppercase">Transaction Success Rate</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm flex flex-col justify-between">
              <div className="p-8">
                <span className="text-xs font-bold text-[#00cfff] tracking-wide uppercase mb-2 block">Logistics & Supply</span>
                <h3 className="text-xl font-bold text-slate-900 mb-4">AI Agent Dispatch Coordinator</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-medium mb-6">
                  Integrated an autonomous AI coordinator into a legacy fleet routing application. Utilized custom RAG parsing vectors for invoice matching, enabling rapid, error-free route dispatch processing.
                </p>
                <div className="text-3xl font-extrabold text-[#0b3b8f] mb-1">-40%</div>
                <div className="text-xs text-slate-500 font-bold uppercase">Fleet Dispatch Delay Time</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm flex flex-col justify-between">
              <div className="p-8">
                <span className="text-xs font-bold text-[#00cfff] tracking-wide uppercase mb-2 block">Healthcare Tech</span>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Compliance-Ready Patient Portal</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-medium mb-6">
                  Rebuilt an enterprise patient portal from scratch using React, Next.js, and Java Spring. Configured federated authentication, granular data encryption audits, and database query optimizations.
                </p>
                <div className="text-3xl font-extrabold text-[#0b3b8f] mb-1">100%</div>
                <div className="text-xs text-slate-500 font-bold uppercase">HIPAA/GDPR Compliance Audit Uptime</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Software Development Resources & Internal Links Section ── */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            Software Development Resources
          </h2>
          <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 max-w-2xl mx-auto mb-10 text-left">
            <h3 className="text-xs font-extrabold text-[#0b3b8f] uppercase tracking-wider mb-2">Featured Article</h3>
            <a 
              href="/blog/how-to-choose-software-development-company" 
              className="text-lg md:text-xl font-extrabold text-slate-900 hover:text-[#00cfff] transition-colors block leading-snug mb-3"
            >
              How to Choose the Right Software Development Company in 2026
            </a>
            <p className="text-slate-600 text-sm leading-relaxed font-medium">
              Read our comprehensive buyer guide detailing pricing models, stack evaluation, project milestones, and contract negotiation checklists.
            </p>
          </div>

          <div className="max-w-2xl mx-auto text-left text-sm text-slate-600 leading-relaxed font-medium space-y-4 border-t border-slate-100 pt-8">
            <p>
              💡 **AI Capabilities**: Our <a href="/services/ai-cloud-consulting" className="text-[#0b3b8f] hover:underline font-bold">AI & Cloud Consulting</a> team can help integrate advanced machine learning models and LLM setups into your existing custom platforms.
            </p>
            <p>
              ⚙️ **DevOps & Infrastructure**: Our custom software teams work closely with <a href="/services/managed-services" className="text-[#0b3b8f] hover:underline font-bold">DevOps / Managed Services</a> specialists to build automated deployment pipelines and configure 24/7 server monitoring.
            </p>
            <p>
              🎓 **Team Training**: Looking to upskill your internal teams on React, Next.js, or Kubernetes? Explore our expert-led <a href="/courses" className="text-[#0b3b8f] hover:underline font-bold">Training Courses</a>.
            </p>
            <p>
              ✉️ **Ready to build?** Drop our engineering team a line via our secure <a href="/contact" className="text-[#0b3b8f] hover:underline font-bold">Contact Page</a> to organize a technical review call.
            </p>
          </div>
        </div>
      </section>

      {/* ── 9. Frequently Asked Questions Section ── */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 font-medium">
              Everything you need to know about our custom software development processes, contracts, and timing.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = activeFaq === idx
              return (
                <div key={idx} className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-300">
                  <button 
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left px-6 py-5 flex justify-between items-center outline-none focus:bg-slate-50/50"
                  >
                    <span className="font-bold text-slate-800 text-lg">{faq.q}</span>
                    <span className="text-xl font-bold text-slate-400 ml-4">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4 font-medium">
                      {faq.a}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── 10. Start Your Software Project Section (CTA) ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b3b8f] via-[#1048a8] to-[#041a4a] text-white py-24 border-t-4 border-[#00cfff]">
        <div className="absolute inset-0 bg-black/10 opacity-10 mix-blend-overlay"></div>
        <div className="relative container mx-auto px-6 text-center z-10 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6">
            Start Your Software Project
          </h2>
          <p className="text-lg md:text-xl text-blue-100/90 leading-relaxed mb-10 max-w-2xl mx-auto font-light">
            Have a software idea, legacy application or product that needs modernization? Talk to our engineering team about your requirements.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button 
              onClick={() => handleOpenModal('Free Consultation')}
              className="group inline-flex items-center justify-center px-10 py-4 bg-white text-[#0b3b8f] rounded-xl font-bold hover:bg-slate-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
            >
              Book a Free Consultation
              <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <button 
              onClick={() => handleOpenModal('Get a Quote')}
              className="group inline-flex items-center justify-center px-10 py-4 border border-white/40 bg-black/20 backdrop-blur-md text-white rounded-xl font-bold hover:bg-white/30 transition-all duration-300 shadow-xl hover:-translate-y-0.5"
            >
              Request a Project Estimate
              <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <OfferModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  )
}
